import { Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, Stack } from "@mui/material";
import { type EmergencyCreditStats } from "@src/appV2/ClipboardScore/api/useGetClipboardScoreProfile";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { Button } from "@src/appV2/redesign/components/Button";
import { CancellationCreditProgressBar } from "@src/appV2/redesign/EmergencyCredits/components/CancellationCreditProgressBar";
import { LatenessCreditProgressBar } from "@src/appV2/redesign/EmergencyCredits/components/LatenessCreditProgressBar";
import { ShieldIcon } from "@src/appV2/redesign/EmergencyCredits/components/ShieldIcon";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { type ReactElement } from "react";

export interface EmergencyCreditsInfoProps {
  emergencyCreditStats: EmergencyCreditStats;
  onLearnMoreClick: () => void;
}

export function EmergencyCreditsInfo(props: EmergencyCreditsInfoProps): ReactElement {
  const { emergencyCreditStats, onLearnMoreClick } = props;
  const { lateness, cancellation } = emergencyCreditStats;
  const { userId: workerId } = useDefinedWorker();

  const shiftCountRequirement =
    cancellation.requiredShiftsForNextCredit === lateness.requiredShiftsForNextCredit
      ? cancellation.requiredShiftsForNextCredit
      : undefined;

  const handleLearnMoreClick = () => {
    logEvent(APP_V2_USER_EVENTS.EMERGENCY_CREDITS_INFO_LEARN_MORE_CLICKED, { workerId });
    onLearnMoreClick();
  };

  return (
    <Stack alignItems="center" spacing={4}>
      <ShieldIcon variant="outlined" sx={{ width: 64, height: 64 }} />

      <Stack alignItems="center" spacing={5} textAlign="center">
        <Text semibold variant="h5">
          Earn Emergency Credits
        </Text>

        <Stack spacing={3}>
          <Text variant="body2">
            Emergency credits significantly reduce the penalty of a late arrival or same-day
            cancellation.
          </Text>
          <Text variant="body2">
            You start with one credit for each category. After using it, you earn it back by
            {isDefined(shiftCountRequirement)
              ? ` completing ${shiftCountRequirement} shifts.`
              : " completing shifts."}
          </Text>
        </Stack>
      </Stack>

      <Box
        sx={(theme) => ({
          width: "100%",
          backgroundColor: theme.palette.background.primary,
          borderRadius: "15px",
          paddingX: 6,
          paddingTop: 6,
          paddingBottom: 8,
        })}
      >
        <Stack spacing={4}>
          <CancellationCreditProgressBar cancellationCreditStats={cancellation} />
          <LatenessCreditProgressBar latenessCreditStats={lateness} />
        </Stack>
      </Box>

      <Button fullWidth variant="outlined" size="medium" onClick={handleLearnMoreClick}>
        Learn More About Emergency Credits
      </Button>
    </Stack>
  );
}
